import { css, html, LitElement } from "lit";
import { customElement, property } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { styleMap } from "lit/directives/style-map.js";
import { shared } from "@pentacode/app/src/styles";
import {
    dateSub,
    formatRange,
    getRange,
    getSubRanges,
    splitRangeByResolution,
    toDateString,
    toTimeString,
} from "@pentacode/core/src/util";
import { DateRange } from "@pentacode/core/src/time";
import { DateString } from "@pentacode/openapi";

export type CalendarEntry<T = unknown> = {
    start: DateString;
    end: DateString;
    time?: {
        start: string;
        end: string;
    };
    title: string;
    icon?: string;
    color?: string;
    data?: T;
};

@customElement("ptc-calendar")
export class Calendar extends LitElement {
    @property({ attribute: false })
    dateRange: DateRange = getRange(new Date(), "month");

    @property({ attribute: false })
    entries: CalendarEntry[] = [];

    private get _months() {
        return splitRangeByResolution(this.dateRange, "month");
    }

    private _resizeObserver = new ResizeObserver(() => this.requestUpdate());

    override connectedCallback(): void {
        super.connectedCallback();
        this._resizeObserver.observe(this);
    }

    override firstUpdated() {
        this.requestUpdate();
    }

    static override styles = [
        shared,
        css`
            :host {
                display: block;
                position: relative;
            }

            .day {
                position: relative;
                flex: 1;
                min-height: 5em;
            }

            .day:hover:has(.click:hover) {
                box-shadow: none;
            }

            .day:not(:last-child) {
                border-right: solid 1px var(--shade-2);
            }

            .week:not(:last-child) {
                border-bottom: solid 1px var(--shade-2);
            }

            .day.today .day-date {
                font-weight: bold;
            }

            .day.other-month::after {
                content: "";
                display: block;
                position: absolute;
                inset: 0 0 0 -1px;
                opacity: 0.5;
                background: var(--color-bg);
                z-index: 2;
                pointer-events: none;
            }
        `,
    ];

    private _renderEntry(entry: CalendarEntry, week: DateRange) {
        const monthEl = this.renderRoot.querySelector(".month") as HTMLElement;
        if (!monthEl) {
            return "";
        }
        const start = entry.start < week.from ? week.from : entry.start;
        const end = entry.end > week.to ? week.to : entry.end;
        const length = dateSub(start, end);
        const dayWidth = monthEl.offsetWidth / 7;
        return html`
            <div
                style="${styleMap({
                    "z-index": 1,
                    width: `${length * dayWidth}px`,
                    "box-sizing": "border-box",
                    "padding-left": start === entry.start ? "0.2em" : "0",
                    "padding-right": end === entry.end ? "0.3em" : "0",
                })}"
            >
                <div
                    class="smaller ${entry.time ? "" : "inverted"} blue box ellipsis click background"
                    @click=${() => this.dispatchEvent(new CustomEvent("entry-clicked", { detail: { entry } }))}
                    style="${styleMap({
                        "border-top-left-radius": start === entry.start ? "0.5em" : "0",
                        "border-bottom-left-radius": start === entry.start ? "0.5em" : "0",
                        "border-top-right-radius": end === entry.end ? "0.5em" : "0",
                        "border-bottom-right-radius": end === entry.end ? "0.5em" : "0",
                        "--color-highlight": entry.color || "var(--blue)",
                        padding: "0.2em",
                        "pointer-events": "auto",
                    })}"
                >
                    ${entry.time
                        ? html`<div class="start-aligning horizontal layout">
                              <i class="${entry.icon || "calendar-day"}" style="margin-right: 0.1em"></i>
                              <div class="stretch collapse">
                                  <div class="ellipsis" style="line-height: 1.6em">${entry.title}</div>
                                  <div class="smaller semibold">
                                      ${toTimeString(new Date(`${entry.start}T${entry.time.start}`))} -
                                      ${toTimeString(new Date(`${entry.start}T${entry.time.end}`))}
                                  </div>
                              </div>
                          </div> `
                        : html`<i class="${entry.icon || "calendar-day"}" style="margin-right: 0.1em"></i
                              >${entry.title}`}
                </div>
            </div>
        `;
    }

    private _renderWeek(week: DateRange, month: DateRange) {
        const days = getSubRanges(week, "day");
        const today = toDateString(new Date());

        const lanes: CalendarEntry[][] = [];
        for (const entry of this.entries.filter((e) => e.start < week.to && e.end > week.from && !e.time)) {
            let lane = lanes.find((entries) => !entries.some((e) => e.start < entry.end && e.end > entry.start));
            if (!lane) {
                lane = [];
                lanes.push(lane);
            }
            lane.push(entry);
        }

        return html` <div class="week stretch evenly stretching horizontal layout">
            ${days.map(({ from: date }) => {
                const entriesWithTime = this.entries.filter((e) => e.start === date && Boolean(e.time));
                const lastPopulatedLaneIndex = [...lanes]
                    .reverse()
                    .findIndex((lane) => lane.some((e) => e.start <= date && e.end > date));
                return html`
                    <div
                        class="${classMap({
                            day: true,
                            today: date === today,
                            "other-month": new Date(date).getMonth() !== new Date(month.from).getMonth(),
                            click: true,
                        })}"
                        @click=${() => this.dispatchEvent(new CustomEvent("day-clicked", { detail: { date } }))}
                    >
                        <div class="margined semibold text-right-aligning day-date">${new Date(date).getDate()}</div>
                        <div class="vertically-margined half-spacing vertical layout">
                            ${lanes
                                .slice(0, lastPopulatedLaneIndex + 1)
                                .map(
                                    (entries) => html`
                                        <div style="height: 1.6em; z-index: 1; pointer-events: none;">
                                            ${entries
                                                .filter(
                                                    (e) =>
                                                        e.start === date ||
                                                        (date === week.from && e.start < date && e.end > date)
                                                )
                                                .map((e) => this._renderEntry(e, week))}
                                        </div>
                                    `
                                )}
                            ${entriesWithTime.map((e) => this._renderEntry(e, week))}
                        </div>
                    </div>
                `;
            })}
        </div>`;
    }

    private _renderMonth(month: DateRange) {
        const fullRange = {
            from: getRange(month.from, "week").from,
            to: getRange(month.to, "week").to,
        } as unknown as DateRange;
        const weeks = getSubRanges(fullRange, "week");

        return html`
            <div class="month border-bottom">
                <div
                    class="larger half-padded semibold text-centering border-bottom sticky-top background"
                    style="z-index: 4;"
                >
                    ${formatRange(month)}
                </div>
                <div
                    class="evenly stretching horizontal layout border-bottom sticky-top background"
                    style="top: 2.1em; z-index: 3;"
                >
                    ${["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"].map(
                        (d) => html` <div class="text-centering half-padded semibold blue colored-text">${d}</div> `
                    )}
                </div>
                <div class="vertical layout">${weeks.map((week) => this._renderWeek(week, month))}</div>
            </div>
        `;
    }

    override render() {
        return html`${this._months.map((r) => this._renderMonth(r))}`;
    }
}
